import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, CalendarDays, Instagram } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import api, { formatApiError } from "../../lib/api";
import { SlotStatusPill } from "./OverviewTab";

const inputCls =
  "w-full bg-obsidian border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-volt/60 transition-colors duration-300";

const EMPTY = { shop_name: "", contact: "", address: "", gstin: "", instagram: false, date: "" };

export default function SlotsTab() {
  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editSlot, setEditSlot] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/slots").then((r) => setSlots(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/slots", form);
      toast.success(`Slot booked for ${form.shop_name}`);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (s) => {
    setEditSlot(s);
    setEditForm({ shop_name: s.shop_name, contact: s.contact, address: s.address, gstin: s.gstin || "", instagram: s.instagram, date: s.date });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/slots/${editSlot.id}`, editForm);
      toast.success("Shop details updated");
      setEditSlot(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/slots/${id}`);
      toast.success("Slot removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="slots-tab" className="grid grid-cols-1 lg:grid-cols-5 gap-8">
      <section className="lg:col-span-2 border border-white/5 bg-panel/40 p-8 h-fit">
        <h3 className="font-display font-semibold text-xl tracking-tight mb-2">Book a Shop Slot</h3>
        <p className="text-sm text-slate-500 mb-8">Register a finalized shop. Admin sees every booking instantly.</p>
        <form onSubmit={submit} data-testid="slot-booking-form" className="space-y-4">
          <input data-testid="slot-shop-name-input" required minLength={2} placeholder="Shop / company name" value={form.shop_name} onChange={(e) => setForm({ ...form, shop_name: e.target.value })} className={inputCls} />
          <input data-testid="slot-contact-input" required minLength={5} placeholder="Contact number" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className={inputCls} />
          <textarea data-testid="slot-address-input" required minLength={5} rows={2} placeholder="Full address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={`${inputCls} resize-none`} />
          <input data-testid="slot-gstin-input" maxLength={15} placeholder="GSTIN (optional)" value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} className={`${inputCls} font-mono uppercase`} />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2 flex items-center gap-2">
              <Instagram size={12} /> Instagram page?
            </p>
            <div className="grid grid-cols-2 gap-3">
              {[true, false].map((v) => (
                <button
                  key={String(v)}
                  type="button"
                  data-testid={v ? "slot-insta-yes" : "slot-insta-no"}
                  onClick={() => setForm({ ...form, instagram: v })}
                  className={`font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-3 border transition-colors duration-300 ${
                    form.instagram === v ? "border-volt/60 text-volt bg-volt/5" : "border-white/10 text-slate-500"
                  }`}
                >
                  {v ? "Yes" : "No"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2 flex items-center gap-2">
              <CalendarDays size={12} /> Slot date
            </p>
            <input data-testid="slot-date-input" required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={`${inputCls} [color-scheme:dark]`} />
          </div>
          <button data-testid="slot-submit-btn" disabled={busy} className="w-full flex items-center justify-center gap-2 bg-volt text-obsidian font-mono text-xs uppercase tracking-[0.2em] font-bold px-8 py-4 hover:bg-[#CCEE00] transition-colors duration-300 disabled:opacity-50">
            <Plus size={14} /> {busy ? "Booking..." : "Book Slot"}
          </button>
        </form>
      </section>

      <section className="lg:col-span-3">
        <h3 className="font-display font-semibold text-xl tracking-tight mb-6">My Booked Slots</h3>
        {slots.length === 0 ? (
          <p className="text-sm text-slate-500 border border-dashed border-white/10 p-12 text-center">No slots booked yet. Your finalized shops will appear here.</p>
        ) : (
          <div className="space-y-4" data-testid="my-slots-list">
            {slots.map((s) => (
              <article key={s.id} data-testid={`slot-item-${s.id.slice(0, 8)}`} className="border border-white/5 bg-panel/40 p-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-obsidian bg-volt font-bold px-3 py-1">{s.date}</span>
                    <h4 className="font-display font-semibold text-lg tracking-tight">{s.shop_name}</h4>
                    <SlotStatusPill status={s.status || "Booked"} />
                  </div>
                  <p className="mt-2 font-mono text-xs text-slate-400">{s.contact}</p>
                  <p className="mt-1 text-sm text-slate-400 max-w-md">{s.address}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {s.gstin && (
                      <span className="font-mono text-[10px] uppercase tracking-[0.15em] border border-white/15 text-slate-400 px-2.5 py-1">GSTIN {s.gstin}</span>
                    )}
                    <span className={`font-mono text-[10px] uppercase tracking-[0.15em] border px-2.5 py-1 ${s.instagram ? "text-volt border-volt/30 bg-volt/10" : "text-slate-500 border-white/10"}`}>
                      Insta: {s.instagram ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button data-testid={`edit-slot-${s.id.slice(0, 8)}`} onClick={() => openEdit(s)} className="text-slate-600 hover:text-volt transition-colors duration-200" aria-label="Edit slot">
                    <Pencil size={16} />
                  </button>
                  <button data-testid={`delete-slot-${s.id.slice(0, 8)}`} onClick={() => remove(s.id)} className="text-slate-600 hover:text-red-400 transition-colors duration-200" aria-label="Delete slot">
                    <Trash2 size={16} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <Dialog open={!!editSlot} onOpenChange={(open) => !open && setEditSlot(null)}>
        <DialogContent className="bg-panel border-white/10 text-slate-50 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl">Edit Shop Details</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveEdit} data-testid="edit-slot-form" className="space-y-4 mt-2">
            <input data-testid="edit-shop-name-input" required minLength={2} placeholder="Shop / company name" value={editForm.shop_name} onChange={(e) => setEditForm({ ...editForm, shop_name: e.target.value })} className={inputCls} />
            <input data-testid="edit-contact-input" required minLength={5} placeholder="Contact number" value={editForm.contact} onChange={(e) => setEditForm({ ...editForm, contact: e.target.value })} className={inputCls} />
            <textarea data-testid="edit-address-input" required minLength={5} rows={2} placeholder="Full address" value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className={`${inputCls} resize-none`} />
            <input data-testid="edit-gstin-input" maxLength={15} placeholder="GSTIN (optional)" value={editForm.gstin} onChange={(e) => setEditForm({ ...editForm, gstin: e.target.value.toUpperCase() })} className={`${inputCls} font-mono uppercase`} />
            <div className="grid grid-cols-2 gap-3">
              {[true, false].map((v) => (
                <button
                  key={String(v)}
                  type="button"
                  data-testid={v ? "edit-insta-yes" : "edit-insta-no"}
                  onClick={() => setEditForm({ ...editForm, instagram: v })}
                  className={`font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-3 border transition-colors duration-300 ${
                    editForm.instagram === v ? "border-volt/60 text-volt bg-volt/5" : "border-white/10 text-slate-500"
                  }`}
                >
                  Instagram: {v ? "Yes" : "No"}
                </button>
              ))}
            </div>
            <input data-testid="edit-date-input" required type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} className={`${inputCls} [color-scheme:dark]`} />
            <button data-testid="edit-slot-submit-btn" disabled={saving} className="w-full bg-volt text-obsidian font-mono text-xs uppercase tracking-[0.2em] font-bold px-8 py-4 hover:bg-[#CCEE00] transition-colors duration-300 disabled:opacity-50">
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
